import type { CityCount } from '@mmef/shared/api';
import { usePageMetadata } from '@/app/usePageMetadata';
import { useGetOverviewQuery } from './eventsApi';
import { HOME_TITLE, homeDescription } from './homeMetadata';
import { UK_TOWNS } from './ukTowns';
import type { Origin } from './filtersSlice';

/** The town list entry for a city, when there is one. */
export function townFor(city: CityCount) {
  return UK_TOWNS.find((town) => town.name.toLowerCase() === city.name.toLowerCase());
}

/**
 * Somewhere to search from, for a city on the homepage.
 *
 * <p>The town list first, because those coordinates are a town centre and a search from a town
 * centre is what the rest of the page assumes. Failing that, the middle of that city's own venues,
 * which the server already works out for the map bubble.
 *
 * <p>The fallback is the point. Only clickable cities were ones in the town list, so New Malden
 * and Walsall drew a bubble that looked like every other bubble and did nothing when pressed —
 * a dead control, and the list is 26 towns against a catalogue that goes wherever organisers go.
 * Null only when the city has no coordinates at all, which means nothing could be geocoded.
 */
export function originFor(city: CityCount): Origin | null {
  const town = townFor(city);
  if (town) return { lat: town.lat, lng: town.lng, label: town.name, source: 'town' };
  if (city.latitude == null || city.longitude == null) return null;
  return { lat: city.latitude, lng: city.longitude, label: city.name, source: 'town' };
}

/** The homepage overview, with the page's title and description set from it. */
export function useOverview() {
  const { data } = useGetOverviewQuery();
  usePageMetadata(data ? { title: HOME_TITLE, description: homeDescription(data) } : {});
  return data;
}
